import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { io } from "socket.io-client";
import api from "../../utils/api"; 

export const NotificationContext = createContext();
export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const socketRef = useRef(null);
  const audioRef = useRef(null);

  const tenantId = localStorage.getItem("tenantId");

  /* ================= SOUND INIT ================= */
  useEffect(() => {
    audioRef.current = new Audio("/notification.mp3");
    audioRef.current.preload = "auto";

    const unlock = () => {
      audioRef.current
        ?.play()
        .then(() => {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        })
        .catch(() => {});
      window.removeEventListener("click", unlock);
    };

    window.addEventListener("click", unlock);

    return () => window.removeEventListener("click", unlock);
  }, []);

  /* ================= FETCH EXISTING ================= */
  const fetchNotifications = async () => {
    try {
      setLoading(true);

      const res = await api.get("/notification");

      const sorted = res.data.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      setNotifications(sorted);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching notifications:", err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  /* ================= SOCKET ================= */
  useEffect(() => {
    socketRef.current = io("/");

    socketRef.current.on("connect", () => {
      socketRef.current.emit("join-tenant", tenantId);
    });

    socketRef.current.on("order-notification", (notification) => {
      handleIncomingNotification(notification);
    });

    return () => socketRef.current.disconnect();
  }, []);

  /* ================= HANDLE INCOMING ================= */
  const handleIncomingNotification = (notification) => {
    const newNotification = {
      ...notification,
      isRead: false,
    };

    setNotifications((prev) => [newNotification, ...prev]);

    showPopup(newNotification);
    playSound();
  };

  /* ================= POPUP ================= */
  const showPopup = (notification) => {
    const popup = {
      _id: notification._id,
      title: notification.title,
      message: notification.message,
      createdAt: notification.createdAt || new Date(),
    };

    setPopups((prev) => [popup, ...prev]);

    setTimeout(() => dismissPopup(popup._id, false), 5000);
  };

  const dismissPopup = (popupId, markRead = false) => {
    setPopups((prev) => prev.filter((p) => p._id !== popupId));
    if (markRead) markAsRead(popupId);
  };

  /* ================= SOUND ================= */
  const playSound = () => {
    try {
      audioRef.current?.play().catch(() => {});
    } catch (err) {
      console.error("Error playing sound:", err);
    }
  };

  /* ================= MARK AS READ ================= */
  const markAsRead = async (notificationId) => {
    try {
      const res = await api.put(`/notification/${notificationId}/read`);

      setNotifications((prev) =>
        prev.map((n) => (n._id === notificationId ? res.data : n))
      );
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  /* ================= CLEAR ALL ================= */
  const deleteAllNotifications = async () => {
    try {
      await api.delete("/notification/clear-all");
      setNotifications([]);
    } catch (err) {
      console.error("Error deleting all notifications", err);
    }
  };

  /* ================= ADD LOCAL ================= */
  const addNotification = useCallback(({ title, message, type }) => {
    const newNotification = {
      _id: Date.now().toString(),
      title,
      message,
      type,
      isRead: false,
      createdAt: new Date(),
    };

    handleIncomingNotification(newNotification);
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        loading,
        addNotification,
        markAsRead,
        deleteAllNotifications,
        popups,
        dismissPopup,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};